import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { InputComponent } from '@3ddv/software-division-components/backoffice/input';
import { CheckboxComponent } from '@3ddv/software-division-components/backoffice/checkbox';
import { FileUploadComponent } from '@3ddv/software-division-components/backoffice/file-upload';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowLeft, lucideEye, lucideLink } from '@ng-icons/lucide';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DialogCloseReason } from 'src/app/private/layout/dialog/app-dialog.service';
import { UtilsService } from 'src/app/private/services/utils.service';
import { GsEventSelectorComponent } from 'src/app/shared/features/event-selector/gs-event-selector.component';
import { Adlink } from '../../models/adlink.model';
import { AdlinkService } from '../../services/adlink.service';

type ModalAction = 'edit' | 'preview';

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.css'],
  imports: [
    BoDialogComponent,
    ButtonComponent,
    InputComponent,
    CheckboxComponent,
    FileUploadComponent,
    ReactiveFormsModule,
    FormsModule,
    GsEventSelectorComponent,
    NgIcon,
  ],
  viewProviders: [provideIcons({ lucideArrowLeft, lucideEye, lucideLink })],
})
export class EditComponent implements OnInit {
  private dialogRef = inject<BrnDialogRef<DialogCloseReason>>(BrnDialogRef);
  private adlinkService = inject(AdlinkService);
  private utils = inject(UtilsService);
  private domSanitizer = inject(DomSanitizer);
  private toastr = inject(ToastrService);

  @Input() public item: Adlink;
  public postImage: File;
  public imagePreview: string | null;
  private subscriptions: Array<Subscription>;
  public selectedEventIds: number[] = [];
  public previewlink: string;
  public hideProtocol: boolean;
  public action: ModalAction = 'edit';
  public previewHtml: SafeHtml;

  public fileUploadEpoch = signal(0);

  public nameControl = new FormControl<string>('', { nonNullable: true });
  public titleControl = new FormControl<string>('', { nonNullable: true });
  public linkControl = new FormControl<string>('', { nonNullable: true });
  public activeControl = new FormControl<boolean>(true, { nonNullable: true });

  constructor() {
    this.subscriptions = [];
    this.hideProtocol = false;
    this.previewlink = '';

    const link$ = this.linkControl.valueChanges.subscribe(link =>
      this.linkChanged(link)
    );
    this.subscriptions.push(link$);
  }

  ngOnInit(): void {
    this.nameControl.setValue(this.item.name ?? '');
    this.titleControl.setValue(this.item.title ?? '');
    this.linkControl.setValue(this.item.link ?? '');
    this.activeControl.setValue(!!this.item.active);

    if (this.item.link) {
      let aux = this.item.link.split('https://');
      if (aux.length === 2) {
        this.hideProtocol = true;
      }
      this.previewlink = this.item.link;
    }
    this.imagePreview = null;
    this.selectedEventIds = this.item.events?.map(e => e.id) || [];
  }

  public onDismiss(reason: DialogCloseReason) {
    this.dialogRef.close(reason);
  }

  public onConfirm() {
    this.item.name = this.nameControl.value;
    this.item.title = this.titleControl.value;
    this.item.link = this.linkControl.value;
    this.item.active = this.activeControl.value;
    let hasError = this.checkInputs(this.item);
    if (hasError) return;
    let aux = this.item.link.split('https://');
    if (aux.length === 1) {
      this.item.link = 'https://' + aux[0];
      this.hideProtocol = true;
    }
    const update$ = this.adlinkService
      .update$(this.item, this.postImage, this.selectedEventIds ?? [])
      .subscribe({
        next: () => {
          this.onDismiss('success');
        },
        error: err => {
          this.toastr.error(this.getApiErrorMessage(err));
        },
      });
    this.subscriptions.push(update$);
  }

  public onFilesChange(files: File[]): void {
    const file = files[0];
    if (!file) {
      this.postImage = null;
      this.imagePreview = null;
      return;
    }
    this.postImage = file;
    const reader = new FileReader();
    this.imagePreview = null;
    reader.readAsDataURL(file);
    reader.onload = () => {
      this.imagePreview = reader.result as string;
    };
  }

  public onEventSelectionChange(ids: number[]) {
    this.selectedEventIds = ids;
  }

  public linkChanged(link: string) {
    if (link) {
      let aux = link.split('https://');
      if (aux.length === 1) {
        this.previewlink = 'https://' + aux[0];
        this.hideProtocol = false;
      } else {
        this.previewlink = 'https://' + aux[1];
        this.hideProtocol = true;
      }
    }
  }

  public removeImage() {
    this.item.urlImage = null;
    this.postImage = null;
    this.imagePreview = null;
    this.fileUploadEpoch.update(epoch => epoch + 1);
  }

  private checkInputs(instance: Adlink): boolean {
    let hasErrors = false;
    this.nameControl.setErrors(null);
    this.titleControl.setErrors(null);
    this.linkControl.setErrors(null);

    if (this.utils.isEmpty(instance.name)) {
      this.nameControl.setErrors({ required: true });
      hasErrors = true;
    }
    if (this.utils.isEmpty(instance.link)) {
      this.linkControl.setErrors({ required: true });
      hasErrors = true;
    }
    if (this.utils.isEmpty(instance.title)) {
      this.titleControl.setErrors({ required: true });
      hasErrors = true;
    }

    return hasErrors;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  public openPreview() {
    this.item.name = this.nameControl.value;
    this.item.title = this.titleControl.value;
    this.item.link = this.linkControl.value;
    if (this.checkInputs(this.item)) {
      return;
    }

    const imageUrl = this.imagePreview || this.item.urlImage || '';
    const link = this.previewlink || this.item.link || '';
    const title = this.item.title || '';
    const name = this.item.name || '';

    const escapedName = this.escapeHtml(name);
    const escapedTitle = this.escapeHtml(title);
    const escapedImageUrl = this.escapeHtml(imageUrl);
    const escapedLink = this.escapeHtml(link);

    const html = `
      <div style="padding: 20px; max-width: 600px; margin: 0 auto; font-family: Arial, sans-serif;">
        <div style="border: 1px solid #ddd; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
          ${
            imageUrl
              ? `<div style="width: 100%; text-align: center; padding: 20px; background-color: #f8f9fa;">
            <img src="${escapedImageUrl}" alt="${escapedName}" style="max-width: 100%; max-height: 300px; object-fit: contain;" />
          </div>`
              : ''
          }
          <div style="padding: 20px;">
            ${name ? `<h3 style="margin: 0 0 10px 0; color: #333; font-size: 18px;">${escapedName}</h3>` : ''}
            ${title ? `<p style="margin: 0 0 15px 0; color: #666; font-size: 14px; line-height: 1.5;">${escapedTitle}</p>` : ''}
            ${
              link
                ? `<div style="margin-top: 15px;">
              <a href="${escapedLink}" target="_blank" style="display: inline-block; padding: 10px 20px; background-color: #007bff; color: white; text-decoration: none; border-radius: 4px; font-size: 14px;">
                Visit Link
              </a>
            </div>`
                : ''
            }
          </div>
        </div>
      </div>
    `;

    this.previewHtml = this.domSanitizer.bypassSecurityTrustHtml(html);
    this.action = 'preview';
  }

  public closePreview() {
    this.action = 'edit';
  }

  private getApiErrorMessage(err: unknown): string {
    const errorBody = (err as { error?: Record<string, unknown> })?.error;

    if (
      Array.isArray(errorBody?.['errors']) &&
      errorBody['errors'].length > 0
    ) {
      return String(
        (errorBody['errors'][0] as { message?: string }).message ??
          'An error occurred'
      );
    }

    const nestedError = errorBody?.['error'] as
      { message?: string } | undefined;
    if (nestedError?.message) {
      return nestedError.message;
    }

    if (typeof errorBody?.['message'] === 'string') {
      return errorBody['message'];
    }

    return 'An error occurred while saving the ad link.';
  }
}
